import { Job, JobTask } from "../types/jobs";
import { countryName } from "./countries";
import { formatQuantity } from "../utils";

type TFunction = (key: string) => string;

const esc = (value: string | null | undefined): string => {
  if (!value) return "";
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${day}-${month}-${year}`;
};

const row = (label: string, value: string): string =>
  `<tr><td class="label">${esc(label)}</td><td>${value}</td></tr>`;

const buildTasksHtml = (tasks: JobTask[], t: TFunction, tenantName: string): string => {
  if (!tasks || tasks.length === 0) return "";

  const taskItems = tasks
    .map((task, idx) => {
      const issuesHtml =
        task.issues && task.issues.length > 0
          ? task.issues.map((i) => `<span class="chip">${esc(i.code ? `${i.title || "—"} (${i.code})` : (i.title || "—"))}</span>`).join(" ")
          : "—";

      const servicesHtml =
        task.services && task.services.length > 0
          ? task.services
              .map((s) => {
                // As on the job's page: the quantity only when it isn't 1.
                const quantity = Number(s.quantity ?? 1);
                const label =
                  quantity !== 1
                    ? `${s.title || "—"} × ${formatQuantity(quantity)}${s.unit ? ` ${s.unit}` : ""}`
                    : s.title || "—";
                return `<span class="chip outlined">${esc(label)}</span>`;
              })
              .join(" ")
          : "—";

      // Equipment with no installation address is not standing at a client's
      // site - it is back with us, so the workspace's own name takes its place.
      const equipmentHtml = task.equipment
        ? `<div><strong>${esc(task.equipment.full_name || "-")}</strong>${
            task.equipment.installation_address
              ? `<div class="indent">${[
                  task.equipment.installation_address.street,
                  task.equipment.installation_address.city,
                  task.equipment.installation_address.zip_code,
                  countryName(task.equipment.installation_address.country),
                ]
                  .filter(Boolean)
                  .map(esc)
                  .join(", ")}</div>${
                  task.equipment.installation_address.email_counter_reminders
                    ? `<div class="indent">${esc(t("Email for counter reminders"))}: ${esc(task.equipment.installation_address.email_counter_reminders)}</div>`
                    : ""
                }`
              : tenantName
                ? `<div class="indent">${esc(tenantName)}</div>`
                : ""
          }</div>`
        : "";

      const COUNTER_KIND_ORDER: Record<string, number> = { BP: 0, CP: 1, SP: 2 };
      const sortedCounters = [...(task.counters ?? [])].sort(
        (a, b) => (COUNTER_KIND_ORDER[a.kind] ?? 99) - (COUNTER_KIND_ORDER[b.kind] ?? 99)
      );
      const counterHtml = sortedCounters
        .map(
          (counter) =>
            `<div>${esc(t("Kind"))}: ${esc(counter.kind_display || "-")}<br/>` +
            `${esc(t("Value"))}: ${esc(String(counter.value ?? "0"))}` +
            (counter.read_at
              ? `<br/>${esc(t("Read at"))}: ${esc(formatDateTime(counter.read_at))}`
              : "") +
            `</div>`
        )
        .join("<br/>");

      const descHtml = task.description
        ? `<div class="pre-wrap">${esc(task.description)}</div>`
        : "";

      const finishedHtml = task.finished_at
        ? `<div>${esc(t("Finished At"))}: ${esc(formatDateTime(task.finished_at))}</div>`
        : "";

      return `
        <div class="task-block">
          <div class="task-title">${esc(t("Task"))} #${idx + 1}</div>
          <table class="info-table">
            ${row(t("Issues"), issuesHtml)}
            ${row(t("Services"), servicesHtml)}
            ${task.equipment ? row(t("Equipment"), equipmentHtml) : ""}
            ${sortedCounters.length > 0 ? row(t("Counter"), counterHtml) : ""}
            ${task.description ? row(t("Description"), descHtml) : ""}
            ${task.finished_at ? row(t("Finished At"), finishedHtml) : ""}
          </table>
        </div>`;
    })
    .join("");

  return `
    <div class="section">
      <h2>${esc(t("Tasks"))} (${tasks.length})</h2>
      ${taskItems}
    </div>`;
};

export const printJob = (job: Job, t: TFunction, tenantName: string = ""): void => {
  // The API returns tasks under either "tasks" or "services" — both are JobTask[].
  // See Job type: `tasks?: JobTask[]; // API returns "tasks"; alias for services`
  const tasks: JobTask[] = job.tasks ?? job.services ?? [];

  const assignedTo =
    job.assigned_to_names && job.assigned_to_names.length > 0
      ? job.assigned_to_names.map((n) => `<span class="chip outlined">${esc(n)}</span>`).join(" ")
      : "-";

  const clientHtml = job.client
    ? esc(job.client.short_name || job.client.name)
    : "-";

  const basicRows = [
    row(t("Number"), esc(job.number)),
    row(t("Status"), esc(job.status_display)),
    row(t("Kind"), esc(job.kind_display)),
    ...(job.client ? [row(t("Client"), clientHtml)] : []),
    ...(job.planned_for
      ? [row(t("Planned For"), esc(formatDate(job.planned_for)))]
      : []),
    ...(job.description ? [row(t("Description"), `<span class="pre-wrap">${esc(job.description)}</span>`)] : []),
    ...(job.complaint_for_number ? [row(t("Complaint For"), esc(job.complaint_for_number))] : []),
    row(t("Created By"), esc(job.created_by?.email || "-")),
    row(t("Finished At"), formatDateTime(job.finished_at) || "-"),
    row(t("Assigned To"), assignedTo),
    ...(job.external_service ? [row(t("External service"), esc(job.external_service.name))] : []),
  ].join("");

  const metadataRows = [
    row(t("Created At"), esc(formatDateTime(job.created_at))),
    row(t("Updated At"), esc(formatDateTime(job.updated_at))),
  ].join("");

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(t("Job Summary"))} – ${esc(job.number)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #222; padding: 24px; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    h2 { font-size: 14px; margin: 16px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
    .header { margin-bottom: 16px; }
    .status { display: inline-block; padding: 2px 8px; border-radius: 4px; background: #e0e0e0; font-size: 11px; margin-left: 8px; vertical-align: middle; }
    .section { margin-bottom: 16px; }
    .info-table { width: 100%; border-collapse: collapse; }
    .info-table td { padding: 4px 8px; vertical-align: top; border: 1px solid #e0e0e0; }
    .info-table td.label { width: 160px; font-weight: 600; color: #555; white-space: nowrap; }
    .task-block { border: 1px solid #ccc; border-radius: 4px; padding: 8px; margin-bottom: 8px; }
    .task-title { font-weight: 600; font-size: 12px; margin-bottom: 6px; }
    .chip { display: inline-block; padding: 1px 6px; border-radius: 10px; background: #e0e0e0; font-size: 11px; margin: 1px; }
    .chip.outlined { background: transparent; border: 1px solid #aaa; }
    .indent { padding-left: 12px; color: #555; }
    .pre-wrap { white-space: pre-wrap; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>${esc(t("Job Summary"))}: ${esc(job.number)}<span class="status">${esc(job.status_display)}</span></h1>
  </div>

  <div class="section">
    <h2>${esc(t("Basic Information"))}</h2>
    <table class="info-table">${basicRows}</table>
  </div>

  ${buildTasksHtml(tasks, t, tenantName)}

  <div class="section">
    <h2>${esc(t("Metadata"))}</h2>
    <table class="info-table">${metadataRows}</table>
  </div>
</body>
</html>`;

  const printWindow = window.open("", "_blank");
  if (!printWindow) return;
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  printWindow.onafterprint = () => printWindow.close();
  printWindow.print();
};
